import styled, { css } from 'styled-components';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { ReadOnlyBanner, useAgentPermission } from '@entities/user';
import { useGetGeoStatsQuery } from '@shared/api';
import { riseIn } from '@shared/theme/animations';
import { IconCrosshair } from '@shared/ui/icons';

const Wrap = styled.div`
  max-width: 1440px;
  margin: 0 auto;
`;

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(5)};
  animation: ${riseIn} 420ms ${({ theme }) => theme.motion.easeOut};
`;

/* Знак агента — визирное перекрестие в квадрате «пункта». */
const Emblem = styled.div`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.primarySoft};
  color: ${({ theme }) => theme.colors.primary};
`;

const Titles = styled.div`
  min-width: 0;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.15;

  & .accent {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 800;
  }
`;

const Subtitle = styled.p`
  margin: ${({ theme }) => theme.spacing(2)} 0 0;
  max-width: 860px;
  font-size: 15px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;
`;

const Tabs = styled.nav`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-bottom: ${({ theme }) => theme.spacing(6)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  overflow-x: auto;
`;

const Tab = styled(Link)<{ $active: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(4)}`};
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  text-decoration: none;
  color: ${({ theme, $active }) => ($active ? theme.colors.text.primary : theme.colors.text.secondary)};
  border-radius: ${({ theme }) => `${theme.radius.sm} ${theme.radius.sm} 0 0`};
  transition: color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }

  /* Активная вкладка — красная «отметка» на линии, как засечка на рейке. */
  &::after {
    content: '';
    position: absolute;
    left: ${({ theme }) => theme.spacing(3)};
    right: ${({ theme }) => theme.spacing(3)};
    bottom: -1px;
    height: 2px;
    border-radius: 2px;
    background: ${({ theme }) => theme.colors.primary};
    transform: scaleX(${({ $active }) => ($active ? 1 : 0)});
    transition: transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};
  }
`;

const Count = styled.span<{ $tone: 'danger' | 'warning' | 'neutral' }>`
  min-width: 22px;
  padding: 1px 7px;
  border-radius: ${({ theme }) => theme.radius.pill};
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  text-align: center;
  ${({ theme, $tone }) =>
    $tone === 'neutral'
      ? css`
          background: ${theme.colors.surface};
          color: ${theme.colors.text.secondary};
        `
      : $tone === 'warning'
        ? css`
            background: ${theme.colors.status.warning}29;
            color: ${theme.colors.text.primary};
          `
        : css`
            background: ${theme.colors.status[$tone]}1A;
            color: ${theme.colors.status[$tone]};
          `}
`;

/**
 * Оболочка ОСНОВА.Геодезист: заголовок, вкладки «Заявки» / «Исполнительные
 * схемы» с бейджами из `/stats`, режим просмотра без прав записи.
 */
export function GeodesyLayout() {
  const { pathname } = useLocation();
  const { can_read, can_write } = useAgentPermission('geodesy');
  const { data: stats } = useGetGeoStatsQuery(undefined, {
    skip: !can_read,
    pollingInterval: 30_000,
  });

  const segments = pathname.split('/').filter(Boolean); // ['agents','geodesy',...]
  const section = segments[2];
  const onRequests = section === 'requests';
  const onSchemes = !onRequests;
  const isCard = section === 'schemes' && segments.length > 3;

  const incoming = stats?.incoming_requests ?? 0;
  const attention = stats?.needs_attention ?? 0;

  return (
    <Wrap>
      <Header>
        <Emblem aria-hidden>
          <IconCrosshair size={24} />
        </Emblem>
        <Titles>
          <Title>
            ОСНОВА<span className="accent">.Геодезист</span>
          </Title>
          {!isCard && (
            <Subtitle>
              Камеральная работа геодезиста: заявки на съёмку и исполнительные схемы по
              ГОСТ&nbsp;Р&nbsp;51872-2024 — сличение съёмки с&nbsp;рабочей документацией, отклонения
              с&nbsp;допусками по&nbsp;СП, DXF и&nbsp;PDF для заказчика. Числа считает код, модель
              только пересказывает.
            </Subtitle>
          )}
        </Titles>
      </Header>

      <Tabs aria-label="Разделы ОСНОВА.Геодезист">
        <Tab
          to="/agents/geodesy/requests"
          $active={onRequests}
          aria-current={onRequests ? 'page' : undefined}
        >
          Заявки
          {incoming > 0 && (
            <Count $tone="warning" aria-label={`новых заявок: ${incoming}`}>
              {incoming}
            </Count>
          )}
        </Tab>
        <Tab
          to="/agents/geodesy"
          $active={onSchemes}
          aria-current={onSchemes && !isCard ? 'page' : undefined}
        >
          Исполнительные схемы
          {attention > 0 && (
            <Count $tone="danger" aria-label={`требуют корректировки: ${attention}`}>
              {attention}
            </Count>
          )}
        </Tab>
      </Tabs>

      {can_read && !can_write && <ReadOnlyBanner />}
      <Outlet />
    </Wrap>
  );
}
